// @ts-strict-ignore
import { areEqual } from 'flow/service/util';
import {
  REGEX,
  extractActionPublicId,
  extractThresholdPublicId,
  determineOperationForField,
  extractDataRoutePublicId,
  determineOperationForCredentialOwner,
} from './helpers';
import type {
  AddOperation,
  Change,
  ChangesByActionPublicId,
  RFC6902Operation,
  RemoveOperation,
  ProcessingBuffer,
  ThresholdRemovedChange,
  ThresholdAddedChange,
  ThresholdModifiedChange,
  ActionAddedChange,
  ActionModifiedChange,
  ActionRuleGroupingModifiedChange,
  TagActionGlobalSettingsModifiedChange,
  TagActionLocalSettingsModifiedChange,
  ReplaceOperation,
  SdkConfigurationAddedChange,
  SdkConfigurationModifiedChange,
  SdkConfigurationRemovedChange,
  InputActionMetadataModifiedChange,
  OutputActionMetadataModifiedChange,
  OutputActionExpressionModifiedChange,
  OutcomeActionMetadataModifiedChange,
  ServiceActionOptionChange,
  ServiceActionMetadataChange,
  PublishedAttributeAddedChange,
  ActionRemovedChange,
  PublishedAttributeModifiedChange,
  ActionRuleRemovedChange,
  ActionMLAAddedChange,
  ActionMLAModifiedChange,
  ActionMLARemovedChange,
  WorkflowRoutingActionMetadataModifiedChange,
  ChangelogInputBundle,
  MatrixModelActionMetadataModifiedChange,
  FailureHandlingModeModifiedChange,
  ChangesByOperation,
  OutputActionOptionsModifiedChange,
  MatrixModelActionOptionsModifiedChange,
} from './types';
import {
  Operation,
} from './types';
import {
  createPredicatedProcessor,

} from './lib/predicatedProcessor';
import type { PredicatedProcessor } from './lib/predicatedProcessor';
import {
  MM_KEYS_TO_IGNORE,
  FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
  FAKE_ACTION_ID_FOR_SDK,
  SUPPORTED_ATTRIBUTE_FIELDS_MAP,
  SUPPORTED_ATTRIBUTE_OPTIONS_MAP,
  SUPPORTED_PA_METADATA_FIELDS,
  SUPPORTED_TAG_GLOBAL_SETTINGS_FIELDS,
  VALUE_RANGE_OPTION_FIELD,
} from './constants';
import type { SdkConfigFieldBase } from '../../editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';
import { getFieldsForPlugin, isSdkFieldEmpty, isSdkPluginEmpty } from '../sdk';
import { ActionType } from '../../store/engine/constants';
import { getPATokenFromAttributeAction, isPublishedAttribute } from '../publishedAttributes';
import {
  actionHasRules,
  extractActionTypeFromId,
  getDimensionNumber,
  getMatrixCellLabelValueMap,
  isActionOfType,
  isMatrix1Dimensional,
} from '../actions';
import { isThresholdAnMLA } from '../mla';
import { isDataRouteAnMLA } from '../dataRoutes';
import { isActionOptionAServiceCredential } from '../services';
import type { OutputValueRange } from 'flow/store/engine/types';
import { getRuleIdSet } from '../rules';

export function createChangesBuffer(): ProcessingBuffer<Change, ChangesByActionPublicId> {
  const changes: ChangesByActionPublicId = {};

  return {
    append: change => {
      const arr = Array.isArray(change) ? change : [change];

      arr.forEach(changeToAppend => {
        const { actionPublicId } = changeToAppend;

        if (!Object.hasOwn(changes, actionPublicId)) {
          changes[actionPublicId] = [];
        }

        changes[actionPublicId].push(changeToAppend);
      });
    },
    package: () => changes,
  };
}

export function createChangesByOperationBuffer(): ProcessingBuffer<Change, ChangesByOperation> {
  const changes: ChangesByOperation = {
    [Operation.Add]: [],
    [Operation.Modify]: [],
    [Operation.Remove]: [],
  };

  return {
    append: change => {
      const arr = Array.isArray(change) ? change : [change];

      arr.forEach(changeToAppend => {
        const { op } = changeToAppend;
        changes[op].push(changeToAppend);
      });
    },
    package: () => changes,
  };
}


export default function createOperationProcessors(args: {
  changesBuffer: ProcessingBuffer;
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
},
): Record<string, PredicatedProcessor<RFC6902Operation>> {
  const {
    A: {
      version: versionA,
      props: propsA,
    },
    B: {
      version: versionB,
      props: propsB,
    },
    changesBuffer,
  } = args;

  // explicitly typed function for better generic type safety
  const createOperationProcessor = createPredicatedProcessor<RFC6902Operation>;

  return {
    actionAdded: createOperationProcessor({
      predicates: [
        ({ op, path }) => op === 'add' && REGEX.ACTION_ROOT_PATH.test(path),
      ],
      process: addActionOps => {
        const changes: ActionAddedChange[] = addActionOps.map(addActionOp => {
          const { path } = addActionOp as AddOperation;
          const actionPublicId = extractActionPublicId(path);
          return {
            actionPublicId,
            descriptor: 'action',
            op: Operation.Add,
            value: {
              prev: null,
              next: versionB.actions[actionPublicId],
            },
          };
        });
        changesBuffer.append(changes);
      },
    }),
    actionModified: createOperationProcessor({
      predicates: [
        ({ op, path }) => op === 'replace' && REGEX.ACTION_PATH_TO_NESTED_FIELDS.test(path)
          && !MM_KEYS_TO_IGNORE.some(key => path.endsWith(key)),
      ],
      process: modifyActionOps => {
        const changes: ActionModifiedChange[] = modifyActionOps.map(modifyActionOp => {
          const { path } = modifyActionOp as ReplaceOperation;
          const actionPublicId = extractActionPublicId(path);
          return {
            actionPublicId,
            descriptor: 'action',
            op: Operation.Modify,
            value: {
              prev: versionA.actions[actionPublicId],
              next: versionB.actions[actionPublicId],
            },
          };
        });
        changesBuffer.append(changes);
      },
    }),
    actionRemoved: createOperationProcessor({
      predicates: [
        ({ op, path }) => (op === 'remove' && REGEX.ACTION_ROOT_PATH.test(path)),
      ],
      process: removedActionOps => {
        const changes: ActionRemovedChange[] = removedActionOps.map(removedActionOp => {
          const { path } = removedActionOp as RemoveOperation;
          const actionPublicId = extractActionPublicId(path);

          return {
            op: Operation.Remove,
            actionPublicId,
            descriptor: 'action',
            value: {
              prev: versionA.actions[actionPublicId],
              next: null,
            },
          };
        });
        changesBuffer.append(changes);
      },
    }),
    thresholdAdded: createOperationProcessor({
      predicates: [
        ({ op, path }) => (op === 'add' && REGEX.THRESHOLD_ROOT_PATH.test(path)),
      ],
      process: addedThresholdOps => {
        const changes: ThresholdAddedChange[] = addedThresholdOps.map(addedThresholdOp => {
          const { path } = addedThresholdOp;

          const thresholdPublicId = extractThresholdPublicId(path);
          const threshold = versionB.thresholds[thresholdPublicId];
          const actionPublicId = extractActionPublicId(threshold.actionId);

          return {
            descriptor: 'threshold',
            op: Operation.Add,
            actionPublicId,
            value: {
              prev: null,
              next: threshold,
            },
          };
        });

        changesBuffer.append(changes);
      },

    }),
    thresholdRemoved: createOperationProcessor({
      predicates: [
        ({ op, path }) => (op === 'remove' && REGEX.THRESHOLD_ROOT_PATH.test(path)),
      ],
      process: removedThresholdOps => {
        const changes: ThresholdRemovedChange[] = removedThresholdOps.map(removedThresholdOp => {
          const { path } = removedThresholdOp;

          const thresholdPublicId = extractThresholdPublicId(path);
          const threshold = versionA.thresholds[thresholdPublicId];
          const owningActionPublicId = extractActionPublicId(threshold.actionId);

          return {
            descriptor: 'threshold',
            actionPublicId: owningActionPublicId,
            op: Operation.Remove,
            value: {
              prev: threshold,
              next: null,
            },
          };
        });

        changesBuffer.append(changes);
      },
    }),
    thresholdModified: createOperationProcessor({
      predicates: [
        ({ op, path }) => op === 'replace'
          // only when the change is in a threshold but not at the MLA level
          && REGEX.THRESHOLD_LEFT_RIGHT_OR_OPERATOR_PATH.test(path)
          && !REGEX.ACTION_RULE_MLA_CONTENT.test(path),
      ],
      process: modifiedThresholdOps => {
        const processedThresholdIds = new Set<string>();

        const changes: ThresholdModifiedChange[] = modifiedThresholdOps
          .flatMap(modifiedThresholdOp => {
            const { path } = modifiedThresholdOp;
            const thresholdPublicId = extractThresholdPublicId(path);

            if (processedThresholdIds.has(thresholdPublicId)) {
              return [];
            }

            processedThresholdIds.add(thresholdPublicId);
            const actionPublicId = versionA.thresholds[thresholdPublicId].actionId;

            return [{
              actionPublicId,
              descriptor: 'threshold',
              op: Operation.Modify,
              value: {
                prev: versionA.thresholds[thresholdPublicId],
                next: versionB.thresholds[thresholdPublicId],
              },
            }];
          });

        changesBuffer.append(changes);
      },
    }),
    actionRulesRemoved: createOperationProcessor({
      predicates: [
        ({ op, path }) => {
          if (op !== 'remove') {
            return false;
          }
          const actionPublicId = extractActionPublicId(path);
          const prevAction = versionA.actions[actionPublicId];
          const nextAction = versionB.actions[actionPublicId];
          return (nextAction
            // outputs don't have rules, they have expressions
            && !isActionOfType(nextAction, ActionType.OUTPUT)
            && actionHasRules(prevAction)
            && !actionHasRules(nextAction));
        },
      ],
      process: ruleRemoveOps => {
        const changes: ActionRuleRemovedChange[] = ruleRemoveOps.map(operation => {
          const { path } = operation;

          const actionPublicId = extractActionPublicId(path);
          const action = versionA.actions[actionPublicId];

          return {
            actionPublicId,
            descriptor: 'action.rules',
            op: Operation.Remove,
            value: {
              prev: action.rules,
              next: null,
            },
          };
        });

        changesBuffer.append(changes);
      },
    }),
    actionRuleGroupingModified: createOperationProcessor({
      predicates: [
        /**
         * When a group is modified, more than a single Operation can be yielded. We only
         * need to handle a single one, either an "add" or and "remove" at the action.rules.* path.
         * Going with "add" over "remove" for no specific reason.
         */
        ({ op, path }) => (op === 'add' && REGEX.ACTION_RULE_CONTENT.test(path)),
      ],
      process: ruleModifiedOps => {
        const processedActionPublicIds = new Set<string>();

        const changes: ActionRuleGroupingModifiedChange[] = ruleModifiedOps
          .flatMap(ruleRootAddOp => {
            const { path } = ruleRootAddOp;

            const actionPublicId = extractActionPublicId(path);

            if (processedActionPublicIds.has(actionPublicId)) {
              return [];
            }
            processedActionPublicIds.add(actionPublicId);

            return [{
              descriptor: 'action.rules.grouping',
              actionPublicId,
              op: Operation.Modify,
              value: {
                prev: getRuleIdSet(versionA.actions[actionPublicId]),
                next: getRuleIdSet(versionB.actions[actionPublicId]),
              },
            }];
          });

        changesBuffer.append(changes);
      },
    }),
    actionMLAAdded: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => {
          if (!(op === 'add' && REGEX.THRESHOLD_ROOT_PATH.test(path))) {
            return false;
          }
          const thresholdPublicId = extractThresholdPublicId(path);
          const threshold = versionB.thresholds[thresholdPublicId];
          return isThresholdAnMLA(threshold);
        },
        ({ op, path }) => {
          if (!(op === 'add' && REGEX.DATAROUTE_ROOT_PATH.test(path))) {
            return false;
          }

          const dataRouteId = extractDataRoutePublicId(path);
          const dataRoute = versionB.dataRoutes[dataRouteId];

          return isDataRouteAnMLA(dataRoute);
        },
      ],
      process: (
        mlaAddedInRulesOps,
        mlaAddedInExpressionOps,
      ) => {
        const changesFromRuleOps: ActionMLAAddedChange[] = mlaAddedInRulesOps.map(operation => {
          const { path } = operation;
          const thresholdId = extractThresholdPublicId(path);
          const threshold = versionB.thresholds[thresholdId];

          return {
            actionPublicId: threshold.actionId,
            descriptor: 'action.rules.mla',
            op: Operation.Add,
            value: {
              prev: null,
              next: threshold,
            },
          };
        });

        const changesFromExpressions: ActionMLAAddedChange[] = mlaAddedInExpressionOps
          .map(operation => {
            const { path } = operation;
            const dataRouteId = extractDataRoutePublicId(path);
            const dataRoute = versionB.dataRoutes[dataRouteId];

            return {
              actionPublicId: dataRoute.targetId,
              descriptor: 'action.rules.mla',
              op: Operation.Add,
              value: {
                prev: null,
                next: dataRoute,
              },
            };
          });

        changesBuffer.append(changesFromRuleOps);
        changesBuffer.append(changesFromExpressions);
      },
    }),
    actionMLARemoved: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => {
          if (!(op === 'remove' && REGEX.THRESHOLD_ROOT_PATH.test(path))) {
            return false;
          }
          const thresholdPublicId = extractThresholdPublicId(path);
          const threshold = versionA.thresholds[thresholdPublicId];
          return isThresholdAnMLA(threshold);
        },
        ({ op, path }) => {
          if (!(op === 'remove' && REGEX.DATAROUTE_ROOT_PATH.test(path))) {
            return false;
          }

          const dataRouteId = extractDataRoutePublicId(path);
          const dataRoute = versionA.dataRoutes[dataRouteId];

          return isDataRouteAnMLA(dataRoute);
        },
      ],
      process: (
        mlaRemovedInRulesOps,
        mlaRemovedInExpressionOps,
      ) => {
        const changesFromRuleOps: ActionMLARemovedChange[] = mlaRemovedInRulesOps.map(operation => {
          const { path } = operation;
          const thresholdId = extractThresholdPublicId(path);
          const threshold = versionA.thresholds[thresholdId];

          return {
            actionPublicId: threshold.actionId,
            descriptor: 'action.rules.mla',
            op: Operation.Remove,
            value: {
              prev: threshold,
              next: null,
            },
          };
        });

        const changesFromExpressionOps: ActionMLARemovedChange[] = mlaRemovedInExpressionOps
          .map(operation => {
            const { path } = operation;
            const dataRouteId = extractDataRoutePublicId(path);
            const dataRoute = versionA.dataRoutes[dataRouteId];

            return {
              actionPublicId: dataRoute.targetId,
              descriptor: 'action.rules.mla',
              op: Operation.Remove,
              value: {
                prev: dataRoute,
                next: null,
              },
            };
          });

        changesBuffer.append(changesFromRuleOps);
        changesBuffer.append(changesFromExpressionOps);
      },
    }),
    actionMLAModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ path }) => {
          if (!REGEX.ACTION_RULE_MLA_CONTENT.test(path)) {
            return false;
          }
          const thresholdPublicId = extractThresholdPublicId(path);
          const prevThreshold = versionA.thresholds[thresholdPublicId];
          const nextThreshold = versionB.thresholds[thresholdPublicId];
          return isThresholdAnMLA(prevThreshold) && isThresholdAnMLA(nextThreshold);
        },
        ({ path }) => {
          if (!REGEX.DATAROUTE_MLA_CONTENT.test(path)) {
            return false;
          }
          const dataRouteId = extractDataRoutePublicId(path);
          const prevDataRoute = versionA.dataRoutes[dataRouteId];
          const nextDataRoute = versionB.dataRoutes[dataRouteId];
          return isDataRouteAnMLA(prevDataRoute) && isDataRouteAnMLA(nextDataRoute);
        },
      ],
      process: (
        mlaModifiedInRulesOps,
        mlaModifiedInExpressionOps,
      ) => {
        const processedThresholdIds = new Set<string>();
        const changesFromRules: ActionMLAModifiedChange[] = mlaModifiedInRulesOps
          .flatMap(operation => {
            const { path } = operation;
            const thresholdId = extractThresholdPublicId(path);

            if (processedThresholdIds.has(thresholdId)) {
              return [];
            }
            processedThresholdIds.add(thresholdId);

            const prevMLAThreshold = versionA.thresholds[thresholdId];
            const nextMLAThreshold = versionB.thresholds[thresholdId];

            return {
              actionPublicId: nextMLAThreshold.actionId,
              descriptor: 'action.rules.mla',
              op: Operation.Modify,
              value: {
                prev: prevMLAThreshold,
                next: nextMLAThreshold,
              },
            };
          });

        const processedDataRouteIds = new Set<string>();
        const changesFromExpressions: ActionMLAModifiedChange[] = mlaModifiedInExpressionOps
          .flatMap(operation => {
            const { path } = operation;
            const dataRouteId = extractDataRoutePublicId(path);

            if (processedDataRouteIds.has(dataRouteId)) {
              return [];
            }
            processedThresholdIds.add(dataRouteId);

            const prevMLADataRoute = versionA.dataRoutes[dataRouteId];
            const nextMLADataRoute = versionB.dataRoutes[dataRouteId];

            return {
              actionPublicId: nextMLADataRoute.targetId,
              descriptor: 'action.rules.mla',
              op: Operation.Modify,
              value: {
                prev: prevMLADataRoute,
                next: nextMLADataRoute,
              },
            };
          });

        changesBuffer.append(changesFromRules);
        changesBuffer.append(changesFromExpressions);
      },
    }),
    tagActionLocalSettingsModified: createOperationProcessor({
      predicates: [
        ({ op, path }) => op === 'replace'
          && REGEX.ACTION_SERVICE_FAILURE_DEFAULT.test(path),
      ],
      process: serviceFailureDefaultModifiedOps => {
        const changes: TagActionLocalSettingsModifiedChange[] = serviceFailureDefaultModifiedOps
          .map(serviceFailureDefaultModifiedOp => {
            const { path } = serviceFailureDefaultModifiedOp;
            const actionPublicId = extractActionPublicId(path);
            return {
              actionPublicId,
              descriptor: 'action.tag.localSettings',
              op: Operation.Modify,
              value: {
                prev: {
                  serviceFailureDefault: versionA.actions[actionPublicId].serviceFailureDefault,
                },
                next: {
                  serviceFailureDefault: versionB.actions[actionPublicId].serviceFailureDefault,
                },
              },
            };
          });

        changesBuffer.append(changes);
      },
    }),
    tagActionGlobalSettingsModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => (op === 'replace' && REGEX.TAG_ACTION_GLOBAL_SETTINGS.test(path)),
        ({ op, path }) => ((op === 'add' || op === 'remove')
          && REGEX.ACTION_TAG_GLOBAL_SETTINGS_OUTCOMES.test(path)),
      ],
      process: (globalSettingsGeneralReplaceOps, globalSettingsTagOutcomeOps) => {
        const buildEmptyTagGlobalChange = (
          actionPublicId: string,
        ): TagActionGlobalSettingsModifiedChange => ({
          actionPublicId,
          descriptor: 'action.tag.globalSettings',
          op: Operation.Modify,
          value: {
            prev: {},
            next: {},
          },
        });

        const changeByActionPublicId: Record<string, TagActionGlobalSettingsModifiedChange> = {};

        globalSettingsGeneralReplaceOps.forEach(operation => {
          const { path } = operation;

          const actionPublicId = extractActionPublicId(path);
          const tagField = path.split('/').at(-1);

          if (!SUPPORTED_TAG_GLOBAL_SETTINGS_FIELDS.includes(tagField)) {
            return;
          }

          if (!Object.hasOwn(changeByActionPublicId, actionPublicId)) {
            changeByActionPublicId[actionPublicId] = buildEmptyTagGlobalChange(actionPublicId);
          }

          changeByActionPublicId[actionPublicId].value.prev = {
            ...changeByActionPublicId[actionPublicId].value.prev,
            [tagField]: versionA.actions[actionPublicId].tag[tagField],
          };
          changeByActionPublicId[actionPublicId].value.next = {
            ...changeByActionPublicId[actionPublicId].value.next,
            [tagField]: versionB.actions[actionPublicId].tag[tagField],
          };
        });

        const processedActionPublicIds = new Set<string>();
        globalSettingsTagOutcomeOps.forEach(operation => {
          const { path } = operation;

          const tagActionPublicId = extractActionPublicId(path);

          if (processedActionPublicIds.has(tagActionPublicId)) {
            return;
          }
          processedActionPublicIds.add(tagActionPublicId);

          if (!Object.hasOwn(changeByActionPublicId, tagActionPublicId)) {
            changeByActionPublicId[tagActionPublicId] = buildEmptyTagGlobalChange(
              tagActionPublicId,
            );
          }

          const prevOutcomesNamesForTag = versionA.actions[tagActionPublicId].tag.outcomes;
          const nextOutcomesNamesForTag = versionB.actions[tagActionPublicId].tag.outcomes;

          changeByActionPublicId[tagActionPublicId].value.prev.outcomes = prevOutcomesNamesForTag;
          changeByActionPublicId[tagActionPublicId].value.next.outcomes = nextOutcomesNamesForTag;
        });

        changesBuffer.append(Object.values(changeByActionPublicId));
      },
    }),
    inputOrOutputActionMetadataModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => (
          op === 'replace'
          && REGEX.INPUT_ACTION_PUBLIC_ID.test(path)
          && REGEX.ACTION_PATH_TO_NESTED_FIELDS.test(path)
        ),
        ({ op, path }) => (
          op === 'replace'
          && REGEX.OUTPUT_ACTION_PUBLIC_ID.test(path)
          && REGEX.ACTION_PATH_TO_NESTED_FIELDS.test(path)
        ),
      ],
      process: (
        modifiedInputMetadataOps: ReplaceOperation[],
        modifiedOutputMetadataOps: ReplaceOperation[],
      ) => {
        const processedActionIds = new Set<string>();

        const changes: (
          InputActionMetadataModifiedChange | OutputActionMetadataModifiedChange
        )[] = [
          ...modifiedInputMetadataOps,
          ...modifiedOutputMetadataOps,
        ].flatMap(operation => {
          const { path } = operation;

          const actionPublicId = extractActionPublicId(path);
          if (processedActionIds.has(actionPublicId)) {
            return [];
          }
          processedActionIds.add(actionPublicId);

          const attributeType = REGEX.INPUT_ACTION_PUBLIC_ID.test(path)
            ? ActionType.INPUT
            : ActionType.OUTPUT;

          const supportedAttributeMetadataFields = SUPPORTED_ATTRIBUTE_FIELDS_MAP[attributeType];
          const metadataObjectKeyName = attributeType === ActionType.INPUT
            ? 'input'
            : 'output';

          const prevValue: InputActionMetadataModifiedChange['value']['prev'] | OutputActionMetadataModifiedChange['value']['prev'] = {};
          const nextValue: InputActionMetadataModifiedChange['value']['next'] | OutputActionMetadataModifiedChange['value']['next'] = {
            [Operation.Add]: {},
            [Operation.Modify]: {},
            [Operation.Remove]: {},
          };

          supportedAttributeMetadataFields.forEach(field => {
            // grabs the field at either action.input[field] or action.output[field]
            const prevFieldValue = versionA.actions[actionPublicId][metadataObjectKeyName][field];
            const nextFieldValue = versionB.actions[actionPublicId][metadataObjectKeyName][field];

            // record the prev field/value regardless of the value
            prevValue[field] = prevFieldValue;
            const opForField = determineOperationForField(prevFieldValue, nextFieldValue);
            if (opForField) {
              // only record the next field/value if an operation happened for it
              nextValue[opForField][field] = nextFieldValue;
            }
          });

          const descriptor = attributeType === ActionType.INPUT
            ? 'action.input.metadata'
            : 'action.output.metadata';

          return [{
            actionPublicId,
            op: Operation.Modify,
            descriptor,
            value: {
              prev: prevValue,
              next: nextValue,
            },
          }];
        });

        changesBuffer.append(changes);
      },
    }),
    outputActionOptionModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => (
          op === 'replace'
          && REGEX.OUTPUT_ACTION_OPTIONS_ROOT.test(path)
        ),
        ({ op, path }) => (
          op === 'add'
          && REGEX.OUTPUT_ACTION_OPTIONS_ROOT.test(path)
        ),
      ],
      process: (
        modifiedOutputOptionsOps: ReplaceOperation[],
        addedOutputOptionsOps: AddOperation[],
      ) => {
        const processedActionIds = new Set<string>();

        const changes: (
          OutputActionOptionsModifiedChange
        )[] = [
          ...modifiedOutputOptionsOps,
          ...addedOutputOptionsOps,
        ].flatMap(operation => {
          const { path } = operation;

          const actionPublicId = extractActionPublicId(path);
          if (processedActionIds.has(actionPublicId)) {
            return [];
          }
          processedActionIds.add(actionPublicId);

          const supportedAttributeOptionFields = SUPPORTED_ATTRIBUTE_OPTIONS_MAP.output;

          const prevValue = {};
          const nextValue = {
            [Operation.Add]: {},
            [Operation.Modify]: {},
            [Operation.Remove]: {},
          };

          supportedAttributeOptionFields.forEach(field => {
            // grabs the field at either action.input[field] or action.output[field]
            const prevFieldValue = versionA.actions[actionPublicId].options[field]?.value;
            const nextFieldValue = versionB.actions[actionPublicId].options[field]?.value;
            // record the prev field/value regardless of the value
            prevValue[field] = prevFieldValue;
            const opForField = determineOperationForField(prevFieldValue, nextFieldValue);
            if (opForField) {
              // only record the next field/value if an operation happened for it
              nextValue[opForField][field] = nextFieldValue;
            }
          });

          const descriptor = 'action.output.options';

          return [{
            actionPublicId,
            op: Operation.Modify,
            descriptor,
            value: {
              prev: prevValue,
              next: nextValue,
            },
          }];
        });

        changesBuffer.append(changes);
      },
    }),
    outputActionExpressionModified: createOperationProcessor({
      predicates: [
        ({ path }) => {
          const actionPublicId = extractActionPublicId(path);
          if (extractActionTypeFromId(actionPublicId) !== ActionType.OUTPUT) {
            return false;
          }

          const prevExpression = versionA.actions[actionPublicId]?.output?.expression;
          const nextExpression = versionB.actions[actionPublicId]?.output?.expression;

          return (
            REGEX.OUTPUT_ACTION_EXPRESSION_ROOT.test(path)
            /**
             * the object diffing library we use [jsondiffpatch](https://github.com/benjamine/jsondiffpatch)
             * handles nested array comparisons in a way where 2 identical (but not the same) arrays
             * produce a diff delta. the library offers a way to customize object/array comparisons
             * via an `objectHash` function param, but using it would mean that all objects and arrays
             * (actions, etc) would need to be hashed before they are compared, and we'd lose some of
             * the fine-grained detail that we rely on. this problem mainly affects nested expressions
             * (expression groups), so the way we solve it is by running a manual comparison against
             * expressions in output actions - this would have been handled by the diffing library.
             */
            && !areEqual(prevExpression, nextExpression)
          );
        },
      ],
      process: modifiedOutputExpressionOps => {
        const processedActionIds = new Set<string>();
        const changes: OutputActionExpressionModifiedChange[] = modifiedOutputExpressionOps
          .flatMap(operation => {
            const { path } = operation;

            const actionPublicId = extractActionPublicId(path);

            if (processedActionIds.has(actionPublicId)) {
              return [];
            }
            processedActionIds.add(actionPublicId);

            return [{
              actionPublicId,
              descriptor: 'action.output.expression',
              op: Operation.Modify,
              value: {
                prev: versionA.actions[actionPublicId].output.expression,
                next: versionB.actions[actionPublicId].output.expression,
              },
            }];
          });

        changesBuffer.append(changes);
      },
    }),
    outcomeActionMetadataModified: createOperationProcessor({
      predicates: [
        ({ op, path }) => (
          op === 'replace'
          && REGEX.ACTION_PATH_TO_NESTED_FIELDS.test(path)
          && REGEX.OUTCOME_ACTION_PUBLIC_ID.test(path)
        ),
      ],
      process: modifiedOutcomeMetadataOps => {
        const processedActionIds = new Set<string>();
        const changes: OutcomeActionMetadataModifiedChange[] = modifiedOutcomeMetadataOps
          .flatMap(operation => {
            const { path } = operation;

            const actionPublicId = extractActionPublicId(path);
            if (processedActionIds.has(actionPublicId)) {
              return [];
            }

            processedActionIds.add(actionPublicId);

            return [{
              actionPublicId,
              descriptor: 'action.outcome.metadata',
              op: Operation.Modify,
              value: {
                prev: {
                  precedenceIndex: versionA.actions[actionPublicId].outcome.precedenceIndex,
                },
                next: {
                  precedenceIndex: versionB.actions[actionPublicId].outcome.precedenceIndex,
                },
              },
            }];
          });

        changesBuffer.append(changes);
      },
    }),
    workflowRoutingActionMetadataModified: createOperationProcessor({
      predicates: [
        ({ op, path }) => (
          op === 'replace'
          && REGEX.ACTION_PATH_TO_NESTED_FIELDS.test(path)
          && REGEX.WORKFLOW_ROUTING_ACTION_PUBLIC_ID.test(path)
        ),
      ],
      process: modifiedWorkflowRoutingMetadataOps => {
        const processedActionIds = new Set<string>();
        const changes: (
          WorkflowRoutingActionMetadataModifiedChange
        )[] = modifiedWorkflowRoutingMetadataOps
          .flatMap(operation => {
            const { path } = operation;

            const actionPublicId = extractActionPublicId(path);
            if (processedActionIds.has(actionPublicId)) {
              return [];
            }

            processedActionIds.add(actionPublicId);

            return [{
              actionPublicId,
              descriptor: 'action.workflow.metadata',
              op: Operation.Modify,
              value: {
                prev: {
                  precedenceIndex: versionA.actions[actionPublicId].workflow.precedenceIndex,
                },
                next: {
                  precedenceIndex: versionB.actions[actionPublicId].workflow.precedenceIndex,
                },
              },
            }];
          });

        changesBuffer.append(changes);
      },
    }),
    publishedAttributeAdded: createOperationProcessor({
      predicates: [
        /**
         * For now, we only support publishing an attribute at the same time it's added.
         * Hence, this predicate looks for action added ops that are a published attribute.
         */
        ({ op, path }) => {
          if (!(op === 'add' && REGEX.ACTION_ROOT_PATH.test(path))) {
            return false;
          }
          const actionPublicId = extractActionPublicId(path);
          const action = versionB.actions[actionPublicId];
          return (isPublishedAttribute(action));
        },
      ],
      process: addedPublishedAttributeActionOps => {
        const changes: PublishedAttributeAddedChange[] = addedPublishedAttributeActionOps
          .map(operation => {
            const { path } = operation;
            const actionPublicId = extractActionPublicId(path);
            const action = versionB.actions[actionPublicId];

            const publishedAttributeToken = getPATokenFromAttributeAction(action);
            const publishedAttribute = propsB.publishedAttributes
              .find(pa => pa.attributeToken === publishedAttributeToken);


            return {
              actionPublicId,
              descriptor: 'publishedAttribute',
              op: Operation.Add,
              value: {
                prev: null,
                next: publishedAttribute,
              },
            };
          });


        changesBuffer.append(changes);
      },
    }),
    publishedAttributeModified: createOperationProcessor({
      predicates: [
        ({ path, op }) => op === 'replace' && REGEX.PUBLISHED_ATTRIBUTE_PATH_TO_FIELDS.test(path),
      ],
      process: paFieldOps => {
        const processedPAIndices = new Set<number>();

        const changes: PublishedAttributeModifiedChange[] = paFieldOps.flatMap(operation => {
          const { path } = operation;

          const [paIndexStr] = path.match(/\d+/);
          const paIndex = Number(paIndexStr);
          if (isNaN(paIndex)) {
            throw new Error('could not find grab numeric index from operation');
          }

          if (processedPAIndices.has(paIndex)) {
            return [];
          }
          processedPAIndices.add(paIndex);

          // when a PA is modified, it is guaranteed to exist on both prev and next WF props
          const prevPublishedAttribute = propsA.publishedAttributes[paIndex];
          // the index of the PA is not guaranteed to be the same, so we search by token
          const nextPublishedAttribute = propsB.publishedAttributes
            .find(pa => pa.attributeToken === prevPublishedAttribute.attributeToken);

          const publishedAttributeAction = Object.values(versionB.actions).find(action => {
            if (!isPublishedAttribute(action)) {
              return false;
            }
            return getPATokenFromAttributeAction(action) === nextPublishedAttribute.attributeToken;
          });


          let prevValue: PublishedAttributeModifiedChange['value']['prev'] = {};
          let nextValue: PublishedAttributeModifiedChange['value']['next'] = {};

          SUPPORTED_PA_METADATA_FIELDS.forEach(field => {
            const prevFieldValue = prevPublishedAttribute[field];
            const nextFieldValue = nextPublishedAttribute[field];

            const opForField = determineOperationForField(prevFieldValue, nextFieldValue);

            if (!opForField) {
              return;
            }

            prevValue = {
              ...prevValue,
              [field]: prevFieldValue,
            };

            nextValue = {
              ...nextValue,
              [field]: nextFieldValue,
            };
          });

          return [{
            actionPublicId: publishedAttributeAction.publicId,
            descriptor: 'publishedAttribute',
            op: Operation.Modify,
            value: {
              prev: prevValue,
              next: nextValue,
            },
          }];
        });

        changesBuffer.append(changes);
      },
    }),
    serviceActionOptionsModified: createOperationProcessor({
      predicates: [
        ({ path }) => REGEX.SERVICE_ACTION_OPTIONS_ROOT.test(path),
      ],
      /**
           * This process function handles all possible changes for service actions.
           * There are few differences in how these are built, since the data has a
           * consistent shape -- so we deal with them all within the same processor.
           */
      process: modifiedServiceActionOptionsOps => {
        const processedActionIds = new Set<string>();
        const changes: ServiceActionOptionChange[] = modifiedServiceActionOptionsOps
          .flatMap(operation => {
            const { path } = operation;

            const actionPublicId = extractActionPublicId(path);
            if (processedActionIds.has(actionPublicId)) {
              return [];
            }
            processedActionIds.add(actionPublicId);

            // versionA & versionB can be used interchangeably here
            const service = propsB.services[
              versionA.actions[actionPublicId].service.internalName
            ];

            const prevServiceActionOptions = versionA.actions[actionPublicId].options;
            const nextServiceActionOptions = versionB.actions[actionPublicId].options;

            const prevAndNextActionOptionNames = [...new Set([
              ...Object.keys(prevServiceActionOptions),
              ...Object.keys(nextServiceActionOptions),
            ])];

            return prevAndNextActionOptionNames.flatMap(optionName => {
              const prevOption = prevServiceActionOptions[optionName];
              const nextOption = nextServiceActionOptions[optionName];

              const opForField = determineOperationForField(prevOption?.value, nextOption?.value);
              if (!opForField) {
                return [];
              }

              const optionMetaData = service.serviceConfig?.serviceConfigOptions.find(
                o => o.serviceConfigOptionId === prevOption?.serviceConfigOptionId
                || o.serviceConfigOptionId === nextOption?.serviceConfigOptionId,
              );

              let descriptor = 'action.service.settings';
              let isSecret = false;
              let isOwner = false;
              let enabledServiceConfig = false; // TODO(DPI-2375): clean the feature flag.
              if (optionMetaData) {
                enabledServiceConfig = true;
                if (optionMetaData.isSecret) {
                  descriptor = 'action.service.credentials';
                  isSecret = true;
                }
                if (optionMetaData.isOwner) {
                  descriptor = 'action.service.owner';
                  isOwner = true;
                }
              } else if (isActionOptionAServiceCredential({ optionName, service })) {
                descriptor = 'action.service.credentials';
                isSecret = true;
              }

              return [{
                actionPublicId,
                descriptor,
                op: opForField,
                optionName,
                isSecret,
                isOwner,
                value: {
                  prev: prevOption,
                  next: nextOption,
                },
                enabledServiceConfig,
              } as ServiceActionOptionChange];
            });
          });

        changesBuffer.append(changes);
      },
    }),
    serviceActionMetadataModified: createOperationProcessor({
      predicates: [
        ({ path }) => REGEX.SERVICE_CREDENTIAL_OWNER.test(path),
      ],
      /**
           * This process function handles all possible changes for service actions.
           * There are few differences in how these are built, since the data has a
           * consistent shape -- so we deal with them all within the same processor.
           */
      process: modifiedServiceCredentialOwnerOps => {
        const processedActionIds = new Set<string>();
        const changes: ServiceActionMetadataChange[] = modifiedServiceCredentialOwnerOps
          .flatMap(operation => {
            const { path } = operation;

            const actionPublicId = extractActionPublicId(path);
            if (processedActionIds.has(actionPublicId)) {
              return [];
            }
            processedActionIds.add(actionPublicId);

            // versionA & versionB can be used interchangeably here
            const service = propsB.services[
              versionA.actions[actionPublicId].service.internalName
            ];

            const enabledServiceConfig = !!service.serviceConfig; // TODO(DPI-2375): clean the feature flag.

            const ownerField = service.serviceConfig?.serviceConfigOptions.find(o => o.isOwner);

            const prevServiceCredentialOwner = versionA.actions[
              actionPublicId].service.credentialOwner;
            const nextServiceCredentialOwner = versionB.actions[
              actionPublicId].service.credentialOwner;
            const prevOwnerField = Object.values(versionA.actions[actionPublicId].options).find(
              o => o.serviceConfigOptionId === ownerField?.serviceConfigOptionId,
            );
            const nextOwnerField = Object.values(versionB.actions[actionPublicId].options).find(
              o => o.serviceConfigOptionId === ownerField?.serviceConfigOptionId,
            );

            const opForField = determineOperationForCredentialOwner(
              prevServiceCredentialOwner, nextServiceCredentialOwner, prevOwnerField,
              nextOwnerField,
            );
            if (!opForField) {
              return [];
            }


            const serviceActionMetadataChanges = [{
              actionPublicId,
              descriptor: 'action.service.credentialOwner',
              op: opForField,
              metadataName: 'Credential Owner',
              value: {
                prev: prevServiceCredentialOwner,
                next: nextServiceCredentialOwner,
              },
              enabledServiceConfig,
            } as ServiceActionMetadataChange];
            return serviceActionMetadataChanges;
          });

        changesBuffer.append(changes);
      },
    }),
    matrixModelActionMetadataModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ path }) => REGEX.MATRIX_MODEL_ACTION_METADATA_ROOT.test(path),
      ],
      process: modifiedMatrixModelActionMetadataOps => {
        const processedActionIds = new Set<string>();

        const changes: (
          MatrixModelActionMetadataModifiedChange
        )[] = modifiedMatrixModelActionMetadataOps.flatMap(operation => {
          const { path } = operation;

          const actionPublicId = extractActionPublicId(path);
          if (processedActionIds.has(actionPublicId)) {
            return [];
          }
          processedActionIds.add(actionPublicId);

          const prevAction = versionA.actions[actionPublicId];
          const nextAction = versionB.actions[actionPublicId];
          const isDataTypeModified = !areEqual(
            prevAction.matrixModel.matrixDataType, nextAction.matrixModel.matrixDataType,
          );

          const isNameModified = !areEqual(
            prevAction.matrixModel.name, nextAction.matrixModel.name,
          );

          const isDimensionsModified = isMatrix1Dimensional(prevAction.matrixModel.axis.x)
            !== isMatrix1Dimensional(nextAction.matrixModel.axis.x);


          const isDataRouteXModified = !areEqual(
            prevAction.matrixModel.dataRouteX, nextAction.matrixModel.dataRouteX,
          );

          const isDataRouteYModified = !areEqual(
            prevAction.matrixModel.dataRouteY, nextAction.matrixModel.dataRouteY,
          );

          const isXAxisConfigurationModified = !areEqual(
            prevAction.matrixModel.axis.x, nextAction.matrixModel.axis.x,
          );

          const isYAxisConfigurationModified = !areEqual(
            prevAction.matrixModel.axis.y, nextAction.matrixModel.axis.y,
          );

          const isConfigurationModified = isDataRouteXModified
            || isDataRouteYModified
            || isXAxisConfigurationModified
            || isYAxisConfigurationModified;

          const matrixCellChanges = {
            [Operation.Add]: {},
            [Operation.Remove]: {},
            [Operation.Modify]: {},
          };

          const nextMatrixMap = getMatrixCellLabelValueMap(nextAction.matrixModel.matrix);
          const prevMatrixMap = getMatrixCellLabelValueMap(prevAction.matrixModel.matrix);

          Object.keys(prevMatrixMap).forEach(cellLabel => {
            // Ignore cells that have been added
            if (prevMatrixMap[cellLabel] === undefined) {
              return;
            }

            if (nextMatrixMap[cellLabel] === undefined) {
              matrixCellChanges.remove[cellLabel] = prevMatrixMap[cellLabel];
              return;
            }

            if (prevMatrixMap[cellLabel] !== nextMatrixMap[cellLabel]) {
              matrixCellChanges.modify[cellLabel] = {
                prev: prevMatrixMap[cellLabel],
                next: nextMatrixMap[cellLabel],
              };
            }
          });

          Object.keys(nextMatrixMap).forEach(cellLabel => {
            // Ignore cells that have been deleted
            if (nextMatrixMap[cellLabel] === undefined) {
              return;
            }

            if (prevMatrixMap[cellLabel] === undefined) {
              matrixCellChanges.add[cellLabel] = nextMatrixMap[cellLabel];
            }
          });


          const haveMatrixCellsChanged = (
            Object.keys(matrixCellChanges.add).length
            || Object.keys(matrixCellChanges.remove).length
            || Object.keys(matrixCellChanges.modify).length
          );

          // for now, we only care about these 4 fields + the generic config.
          // if neither changed, we don't build a Change object
          if (
            !isDataTypeModified
            && !isNameModified
            && !isDimensionsModified
            && !isConfigurationModified
            && !haveMatrixCellsChanged
          ) {
            return [];
          }

          return [{
            actionPublicId,
            descriptor: 'action.matrix.metadata',
            op: Operation.Modify,
            value: {
              prev: {
                ...(isDataTypeModified && { dataType: prevAction.matrixModel.matrixDataType }),
                ...(isDimensionsModified && {
                  dimensions: getDimensionNumber(prevAction.matrixModel.axis.x),
                }),
                ...(isNameModified && { name: prevAction.matrixModel.name }),
              },
              next: {
                ...(haveMatrixCellsChanged && { cellChanges: matrixCellChanges }),
                ...(isConfigurationModified && { configuration: true }),
                ...(isDataTypeModified && { dataType: nextAction.matrixModel.matrixDataType }),
                ...(isDimensionsModified && {
                  dimensions: getDimensionNumber(nextAction.matrixModel.axis.x),
                }),
                ...(isNameModified && { name: nextAction.matrixModel.name }),
              },
            },
          }];
        });


        changesBuffer.append(changes);
      },
    }),
    matrixModelActionOptionsModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ path }) => REGEX.MATRIX_MODEL_ACTION_OPTIONS_ROOT.test(path),
        ({ path }) => REGEX.MATRIX_MODEL_DEFINE_VALUE_RANGES.test(path),
      ],
      process: (
        modifiedMatrixModelActionOptionsOps,
        modifiedMatrixModelDefineValueRangesOps,
      ) => {
        const processedActionIds = new Set<string>();

        const changes: (
          MatrixModelActionOptionsModifiedChange
        )[] = [
          ...modifiedMatrixModelActionOptionsOps,
          ...modifiedMatrixModelDefineValueRangesOps,
        ].flatMap(operation => {
          const { path } = operation;
          const actionPublicId = extractActionPublicId(path);
          if (processedActionIds.has(actionPublicId)) {
            return [];
          }
          processedActionIds.add(actionPublicId);

          const prevAction = versionA.actions[actionPublicId];
          const nextAction = versionB.actions[actionPublicId];

          const isValueRangeConfigRemoved = (
            (prevAction.options.output_value_range?.jsonValue as OutputValueRange[])?.length > 0
            && nextAction.matrixModel.defineValueRanges === false
          );

          const isValueRangeConfigModified = !areEqual(
            prevAction.options.output_value_range?.value ?? [],
            nextAction.options.output_value_range?.value ?? [],
          ) || isValueRangeConfigRemoved;

          // if isValueRangeConfigModified is not modified or removed, we don't build a Change object
          if (!isValueRangeConfigModified) {
            return [];
          }

          return [{
            actionPublicId,
            descriptor: 'action.matrix.options',
            op: Operation.Modify,
            value: {
              prev: {
                ...(isValueRangeConfigModified && {
                  [VALUE_RANGE_OPTION_FIELD]: (
                    prevAction.options.output_value_range?.value as OutputValueRange[]
                  ),
                }),
              },
              next: {
                ...(isValueRangeConfigModified && {
                  [VALUE_RANGE_OPTION_FIELD]: (
                    isValueRangeConfigRemoved
                      ? [] // pass an empty array if the value range config was removed
                      : nextAction.options.output_value_range?.value
                  ) as OutputValueRange[],
                }),
              },
            },
          }];
        });


        changesBuffer.append(changes);
      },
    }),
    sdkConfigAdded: createOperationProcessor({
      predicates: [
        ({ op, path }) => {
          const prevSdkConfig = versionA.sdkConfig;
          const nextSdkConfig = versionB.sdkConfig;

          const isPrevPluginEmpty = isSdkPluginEmpty(prevSdkConfig.plugin);

          const isPluginReplacedByAnother = (
            !isPrevPluginEmpty
            && (prevSdkConfig.plugin !== nextSdkConfig.plugin)
          );

          const isNextPluginEmpty = (
            versionB.sdkConfig.plugin === null
            || versionB.sdkConfig.plugin === ''
          );

          return (op === 'replace'
            && REGEX.SDK_CONFIG_PLUGIN.test(path)
            && !isNextPluginEmpty
            && (isPrevPluginEmpty || isPluginReplacedByAnother)
          );
        },
      ],
      process: addedSdkPluginOps => {
        const [{ value: plugin }] = addedSdkPluginOps as ReplaceOperation<string>[];

        const nextSdkConfig = versionB.sdkConfig;
        const nextFieldsForPlugin = getFieldsForPlugin(nextSdkConfig.fields, nextSdkConfig.plugin);


        const populatedFields = nextFieldsForPlugin.reduce((fields, nextField) => {
          const isFieldPopulated = !isSdkFieldEmpty(nextField);
          if (isFieldPopulated) {
            fields[nextField.displayName] = nextField;
          }
          return fields;
        }, {} as Record<string, SdkConfigFieldBase>);


        const change: SdkConfigurationAddedChange = {
          actionPublicId: FAKE_ACTION_ID_FOR_SDK,
          descriptor: 'sdk',
          op: Operation.Add,
          value: {
            prev: null,
            next: {
              plugin,
              populatedFields,
            },
          },
        };

        changesBuffer.append(change);
      },
    }),
    sdkConfigModified: createOperationProcessor({
      operator: 'some',
      predicates: [
        ({ op, path }) => op === 'replace' && REGEX.SDK_CONFIG_VALUE.test(path),
        ({ op, path }) => op === 'replace' && REGEX.SDK_CONFIG_PLUGIN.test(path),
      ],
      process: (modifiedSdkValueOps, addedSdkPluginOps) => {
        /**
                   * adding an SDK plugin adds many value ops and a single plugin op. the many value ops
                   * would trigger this process function to run, but we only want to build a Modified SDK
                   * change when the values changed without having added a new plugin.
                   *
                   * checking if no added plugin op exists in enough to determine that this is running due
                   * to a modified field only
                   */
        const [addedSdkPluginOp] = addedSdkPluginOps as ReplaceOperation[];
        if (addedSdkPluginOp) {
          return;
        }

        const prevValues: Record<string, SdkConfigFieldBase> = {};
        const nextValues: Record<string, SdkConfigFieldBase> = {};

        const prevSdkConfig = versionA.sdkConfig;
        const nextSdkConfig = versionB.sdkConfig;

        const prevFieldsForPlugin = getFieldsForPlugin(prevSdkConfig.fields, prevSdkConfig.plugin);
        const nextFieldsForPlugin = getFieldsForPlugin(nextSdkConfig.fields, nextSdkConfig.plugin);


        nextFieldsForPlugin.forEach(nextField => {
          const prevField = prevFieldsForPlugin.find(f => f.slug === nextField.slug);

          /**
                     * the sdk config logic can sometimes represent an empty field as an empty string or null,
                     * so it's possible that while they are different, both are still empty
                     */
          const isFieldModified = !areEqual(prevField, nextField);
          const isFieldNullishInBothStates = isSdkFieldEmpty(prevField)
            && isSdkFieldEmpty(nextField);
          // if the field equals the default value, it wasn't changed by the user so we ignore it
          const isValueSameAsDefault = prevField.defaultValue === nextField.value;

          if (
            !isFieldModified
            || isFieldNullishInBothStates
            || isValueSameAsDefault) {
            return;
          }

          // prev and next can be used interchangeably here
          const fieldName = prevField.displayName;

          prevValues[fieldName] = prevField;
          nextValues[fieldName] = nextField;
        });

        const change: SdkConfigurationModifiedChange = {
          actionPublicId: FAKE_ACTION_ID_FOR_SDK,
          descriptor: 'sdk',
          op: Operation.Modify,
          value: {
            prev: prevValues,
            next: nextValues,
          },
        };

        changesBuffer.append(change);
      },
    }),
    sdkConfigRemoved: createOperationProcessor({
      predicates: [
        ({ op, path }) => {
          const prevPlugin = versionA.sdkConfig.plugin;
          const nextPlugin = versionB.sdkConfig.plugin;

          const isPrevPluginEmpty = isSdkPluginEmpty(prevPlugin);
          const isNextPluginEmpty = isSdkPluginEmpty(nextPlugin);

          const isPluginReplacedByAnother = (
            (!isPrevPluginEmpty && !isNextPluginEmpty)
            && (prevPlugin !== nextPlugin)
          );

          return (
            op === 'replace'
            && REGEX.SDK_CONFIG_PLUGIN.test(path)
            && (isNextPluginEmpty || isPluginReplacedByAnother)
          );
        },
      ],
      process: () => {
        const change: SdkConfigurationRemovedChange = {
          actionPublicId: FAKE_ACTION_ID_FOR_SDK,
          descriptor: 'sdk',
          op: Operation.Remove,
          value: {
            prev: versionA.sdkConfig.plugin,
            next: null,
          },
        };

        changesBuffer.append(change);
      },
    }),
    failureHandlingModeModified: createOperationProcessor({
      predicates: [
        ({ op }) => {
          const prevFailureHandlingMode = versionA.failureHandlingMode;
          const nextFailureHandlingMode = versionB.failureHandlingMode;

          const isFailureHandlingModeChanged = prevFailureHandlingMode !== nextFailureHandlingMode;

          return (op === 'replace'
            && isFailureHandlingModeChanged
          );
        },
      ],
      process: () => {
        const change: FailureHandlingModeModifiedChange = {
          actionPublicId: FAKE_ACTION_ID_FOR_FAILURE_HANDLING_MODE,
          descriptor: 'failureHandling',
          op: Operation.Modify,
          value: {
            prev: versionA.failureHandlingMode,
            next: versionB.failureHandlingMode,
          },
        };

        changesBuffer.append(change);
      },
    }),
  };
}
